import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Chip, IconButton } from "@mui/material";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MailIcon from '@mui/icons-material/Mail';
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import { useLazyGetFilteredNotesBySourceQuery } from "store/services/noteApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useLazyGetBranchByRegionDDLQuery } from "store/services/branchApi";

const FilteredNotesList = () => {
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const { isVendor, viewEntity } = useAuth();

  const responseTab = searchParams.get("tab") || "Not Responded";
  const keyword = searchParams.get("search") || "";

  const [branches, setBranches] = React.useState([]);
  const [user, setUser] = React.useState("");
  const [vendor, setVendor] = React.useState("");
  const [province, setProvince] = React.useState("");

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Shipment Notes" },
  ];
  const NotesListTabs = [
    { label: "Not Responded", value: "Not Responded" },
    { label: "Responded", value: "Responded" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipment?.shipmentId,
      cell: (row) => {
        const displayId = row?.shipment?.shipmentId;
        const path = isVendor
          ? `/app/vendor/shipment/${row?.shipment?._id}/detail`
          : `/app/crm/shipment/${row?.shipment?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
      minWidth: "200px",
    },
    {
      name: "Content",
      selector: (row) => row?.content,
      cell: (row) => (
        <div
          title={row?.content?.replace(/<[^>]+>/g, "")}
          className="line-clamp-2"
          dangerouslySetInnerHTML={{ __html: row?.content || "" }}
        />
      ),
      minWidth: "250px",
    },
    {
      name: "Branch",
      selector: (row) => row?.shipment?.sourceBranch?.name || row?.shipment?.destinationBranch?.name,
      cell: (row) => (
        <div
          title={`${row?.shipment?.sourceBranch?.name} - ${row?.shipment?.destinationBranch?.name}`}
          className="line-clamp-2"
        >
          {row?.shipment?.sourceBranch || row?.shipment?.destinationBranch ? (
            <HighlightedText
              text={`${row?.shipment?.sourceBranch?.name} - ${row?.shipment?.destinationBranch?.name}`}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Vendor",
      selector: (row) => row?.shipment?.vendor,
      cell: (row) => (
        <div title={row?.shipment?.vendor?.ownerName} className="line-clamp-2">
          {row?.shipment?.vendor ? (
            <HighlightedText text={row?.shipment?.vendor?.ownerName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row?.type,
      cell: (row) => row?.type || "N/A",
    },
    // Inactive tab mixes responded and not-responded notes, so surface the
    // note's responded state as a Yes/No chip (same field the tab filters use).
    ...(responseTab === "Inactive"
      ? [
        {
          name: "Responded",
          selector: (row) => row?.responded,
          cell: (row) => (
            <Chip
              label={row?.responded ? "Yes" : "No"}
              color={row?.responded ? "success" : "default"}
              title={row?.responded ? "Yes" : "No"}
              size="small"
              sx={{
                minWidth: "70px",
                height: 20,
                fontSize: "0.7rem",
                borderRadius: "25px",
              }}
            />
          ),
        },
      ]
      : []),
    {
      name: "Show Client",
      selector: (row) => row?.showClient,
      cell: (row) => (
        <Chip
          label={row?.showClient ? "Yes" : "No"}
          color={row?.showClient ? "success" : "default"}
          title={row?.showClient ? "Yes" : "No"}
          size="small"
          sx={{
            minWidth: "70px",
            height: 20,
            fontSize: "0.7rem",
            borderRadius: "25px",
          }}
        />
      ),
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Replies",
      selector: (row) => row?.replies?.length || 0,
      cell: (row) => (
        <Badge
          badgeContent={row?.replies?.length || 0}
          color={row?.replies?.length > 0 ? "primary" : "default"}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiBadge-badge": {
              minWidth: "16px",
              height: "16px",
              fontSize: "0.7rem",
              borderRadius: "8px",
              padding: "0 4px",
            },
          }}
        >
          <MailIcon fontSize="small" color={row?.replies?.length > 0 ? "primary" : "disabled"} />
        </Badge>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              isVendor
                ? `/app/vendor/shipment/${row?.shipment?._id}/detail`
                : `/app/crm/shipment/${row?.shipment?._id}/detail`
            }
          >
            <IconButton size="small" aria-label="View Note">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
        </>
      ),
    },
  ];

  const [triggerNotes, result] = useLazyGetFilteredNotesBySourceQuery();
  const { data: notesData, isLoading: loadingNotes, isFetching: fetchingNotes, error } = result;

  // Fetch branches, users, vendors, and regions
  const [fetchBranches, { data: branchesData, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const [fetchUsers, { data: usersData, isLoading: loadingUsers }] =
    useLazyGetDDLUserListQuery();
  const [fetchVendors, { data: vendorsData, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();
  const [triggerGetDdlRegionList, { data: regionsData }] =
    useLazyGetDdlRegionListQuery();
  const [triggerGetBranchesByRegion, { data: provinceBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();

  // Mapped region list
  const mappedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];

    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({
        label: item.name,
        value: item._id,
      }));
  }, [regionsData]);

  // Mapped province branches
  const mappedProvinceBranches = React.useMemo(
    () =>
      Array.isArray(provinceBranchesData?.data)
        ? provinceBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({
            label: `${item.name} - ${item.code}`,
            value: item._id,
            _id: item._id,
            name: item.name,
          }))
        : [],
    [provinceBranchesData]
  );

  // Selected province
  const selectedProvince = React.useMemo(
    () => mappedRegionList?.find((region) => region.value === province),
    [mappedRegionList, province]
  );

  // Branch IDs from province
  const branchIdsFromProvince = React.useMemo(
    () => mappedProvinceBranches.map((branch) => branch._id),
    [mappedProvinceBranches]
  );

  // Fetch regions
  const fetchRegions = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch regions:", error);
        });
    }
  };

  // Handle province change
  const handleProvinceChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";
    setProvince(regionId);
    setBranches([]); // Clear branch selection when province changes

    if (regionId && viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch province branches:", error);
        });
    }
  };

  useEffect(() => {
    fetchBranches({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
    fetchUsers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
    fetchVendors({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    fetchBranches,
    fetchUsers,
    fetchVendors,
  ]);

  useEffect(() => {
    // Use province branches if province is selected, otherwise use manual branch selection
    const finalBranchIds = province
      ? branchIdsFromProvince
      : branches.map((branch) => branch._id);

    triggerNotes({
      source: "Shipment",
      entity: viewEntity.entity._id,
      // Inactive tab shows notes of deactivated shipments regardless of
      // responded status — the other tabs keep the responded filter.
      ...(responseTab !== "Inactive" && {
        active: responseTab === "Responded" ? "true" : "false",
      }),
      shipmentActive: responseTab === "Inactive" ? "inactive" : "active",
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      ...(finalBranchIds.length > 0 && { branch: finalBranchIds }),
      ...(user && { user }),
      ...(vendor && { vendor }),
    });
  }, [
    triggerNotes,
    responseTab,
    keyword,
    currentPage,
    perPage,
    branches,
    province,
    branchIdsFromProvince,
    user,
    vendor,
    viewEntity?.entity?._id,
  ]);

  useAppMeta(`${responseTab} Notes`);

  // Prepare options for branch, user, and vendor dropdowns
  const branchOptions = React.useMemo(() => {
    return (branchesData || [])
      .filter((branch) => branch && branch._id && branch.name)
      .filter((branch, index, self) => index === self.findIndex((b) => b._id === branch._id));
  }, [branchesData]);

  const userOptions = React.useMemo(() => {
    const validUsers = Array.isArray(usersData)
      ? usersData.map((item) => ({
        label: item?.name,
        value: item?._id,
      }))
      : [];
    return [{ label: "All Users", value: "" }, ...validUsers];
  }, [usersData]);

  const vendorOptions = React.useMemo(() => {
    const validVendors = Array.isArray(vendorsData)
      ? vendorsData.map((item) => ({
        label: item?.ownerName,
        value: item?._id,
      }))
      : [];
    return [{ label: "All Vendors", value: "" }, ...validVendors];
  }, [vendorsData]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error
        error={
          error || (!viewEntity?.entity?._id ? { message: "No entity ID available for fetching notes" } : null)
        }
      />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={NotesListTabs} activeTab={responseTab} />
        <TableFilter searchLabel="Shipment ID" initialFilters={3}>
          {/* Updated Branch filter to use BaseAutocomplete */}
          <BaseAutocomplete
            fullWidth
            multiple
            label="Branches"
            value={branches}
            onChange={(_, data) => {
              setBranches(data);
              setProvince(""); // Clear province when manual branch selection
            }}
            getOptionLabel={(option) => option.name || "Unknown Branch"}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            options={branchOptions}
            loading={loadingBranches}
            loadingText="Loading Branches..."
            disabled={loadingBranches || loadingNotes || fetchingNotes || !!province}
          />

          <BaseAutocomplete
            fullWidth
            label="Province"
            value={selectedProvince || null}
            options={mappedRegionList}
            onChange={handleProvinceChange}
            onFocus={fetchRegions}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
            disabled={loadingNotes || fetchingNotes}
          />

          <BaseAutocomplete
            fullWidth
            label="Created By"
            value={userOptions.find((opt) => opt.value === user) || null}
            options={userOptions}
            onChange={(_, selected) => {
              setUser(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown User"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingUsers}
          />

          <BaseAutocomplete
            fullWidth
            label="Vendor"
            value={vendorOptions.find((opt) => opt.value === vendor) || null}
            options={vendorOptions}
            onChange={(_, selected) => {
              setVendor(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown Vendor"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingVendors}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={notesData?.data || []}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={notesData?.totalItems}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loadingNotes || fetchingNotes || loadingBranches || loadingUsers || loadingVendors}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default FilteredNotesList;